import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { listPaymentGateways, createPaymentOrder, verifyPayment } from '../../lib/api';

const fmt = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 0 })}`;

const GATEWAY_META = {
  razorpay: { color: '#3395FF', icon: 'Rz', tagline: 'UPI · Cards · NetBanking' },
  cashfree: { color: '#FF6B35', icon: 'Cf', tagline: 'UPI · Cards · NetBanking' },
  payu:     { color: '#8B5CF6', icon: 'PU', tagline: 'UPI · Cards · Wallets' },
  mock:     { color: '#6b7280', icon: '🎭', tagline: 'Demo mode (approval-ready)' },
};

/**
 * PaymentCheckoutModal
 *   props: { open, onClose, amount, purpose, billContext, onSuccess }
 *     amount:      rupees (number)
 *     purpose:     'wallet_topup' | 'direct_bill'
 *     billContext: { biller_id, customer_id, customer_name? } — required for direct_bill
 *     onSuccess:   callback with verified response
 */
export default function PaymentCheckoutModal({ open, onClose, amount, purpose, billContext, onSuccess }) {
  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState('mock');
  const [step, setStep] = useState('choose'); // choose | processing | mock-modal
  const [orderInfo, setOrderInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mockChoice, setMockChoice] = useState('upi'); // upi | card | netbanking
  const [mockUpi, setMockUpi] = useState('demo@upi');
  const [mockCard, setMockCard] = useState('4111 1111 1111 1111');

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await listPaymentGateways();
        const list = res.data.gateways || [];
        setGateways(list);
        // Default: first configured real gateway, else mock
        const preferred = list.find(g => g.configured && g.name !== 'mock') || list.find(g => g.name === 'mock');
        setSelectedGateway(preferred?.name || 'mock');
        setStep('choose');
        setOrderInfo(null);
      } catch (err) {
        toast.error('Gateways load fail');
      }
    })();
  }, [open]);

  if (!open) return null;

  const chosenAdapter = gateways.find(g => g.name === selectedGateway);
  const isPurposeTopup = purpose === 'wallet_topup';

  const startCheckout = async () => {
    try {
      setBusy(true);
      const res = await createPaymentOrder({
        amount,
        purpose,
        gateway: selectedGateway,
        bill_context: billContext || null,
      });
      setOrderInfo(res.data);

      // If backend fell back to mock (real gateway not configured), we get gateway='mock' back
      if (res.data.gateway === 'mock') {
        setStep('mock-modal');
        return;
      }

      // Real Razorpay checkout
      if (res.data.gateway === 'razorpay') {
        await openRazorpayCheckout(res.data);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Order create fail');
      setStep('choose');
    } finally { setBusy(false); }
  };

  const openRazorpayCheckout = (order) => new Promise((resolve, reject) => {
    // Dynamically inject the Razorpay script if not already loaded
    const openIt = () => {
      const options = {
        key: order.key_id_for_frontend,
        amount: order.amount_paise,
        currency: order.currency,
        order_id: order.order_id,
        name: 'BBPS Bill Pay',
        description: purpose === 'wallet_topup' ? `Wallet Top-up ${fmt(amount)}` : `Bill Payment ${fmt(amount)}`,
        handler: async (rzpResponse) => {
          try {
            const v = await verifyPayment({
              gateway: 'razorpay',
              order_id: rzpResponse.razorpay_order_id,
              payment_id: rzpResponse.razorpay_payment_id,
              signature: rzpResponse.razorpay_signature,
            });
            toast.success('Payment successful ✓');
            onSuccess?.(v.data);
            onClose?.();
            resolve(v.data);
          } catch (err) {
            toast.error(err?.response?.data?.detail || 'Payment verification failed');
            reject(err);
          }
        },
        modal: {
          ondismiss: () => { setStep('choose'); toast.info('Checkout cancelled'); },
        },
        theme: { color: '#7c3aed' },
      };
      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', (resp) => { toast.error(resp.error?.description || 'Payment failed'); setStep('choose'); reject(resp); });
      rzp.open();
    };

    if (window.Razorpay) return openIt();
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.onload = openIt;
    s.onerror = () => { toast.error('Razorpay script load fail'); reject(); };
    document.body.appendChild(s);
  });

  // Mock checkout: simulate success
  const simulateMockPay = async () => {
    if (!orderInfo) return;
    try {
      setBusy(true);
      const v = await verifyPayment({
        gateway: 'mock',
        order_id: orderInfo.order_id,
        payment_id: `pay_mock_${Date.now()}`,
        signature: 'mock',
      });
      toast.success('Payment successful ✓');
      onSuccess?.(v.data);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Verify fail');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-3 sm:p-4" data-testid="payment-checkout-modal">
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 p-4 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-wider text-white/80">
                {isPurposeTopup ? 'Wallet Top-up' : 'Direct Bill Pay'}
              </p>
              <p className="text-3xl font-bold mt-1" data-testid="checkout-amount">{fmt(amount)}</p>
              {!isPurposeTopup && billContext && (
                <p className="text-xs text-white/80 mt-1">{billContext.customer_id} · {billContext.customer_name || ''}</p>
              )}
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white text-2xl">✕</button>
          </div>
        </div>

        {/* STEP: choose gateway */}
        {step === 'choose' && (
          <div className="p-4 space-y-3">
            <p className="text-xs font-semibold text-gray-800 uppercase tracking-wider">Choose Payment Gateway</p>
            <div className="space-y-2" data-testid="gateway-list">
              {gateways.map((g) => {
                const meta = GATEWAY_META[g.name] || GATEWAY_META.mock;
                const isSelected = selectedGateway === g.name;
                const isPlaceholder = !g.configured && g.name !== 'mock';
                return (
                  <button
                    key={g.name}
                    onClick={() => { if (!isPlaceholder) setSelectedGateway(g.name); }}
                    disabled={isPlaceholder}
                    data-testid={`gateway-${g.name}`}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left
                      ${isSelected ? 'border-purple-500 bg-purple-50 shadow-sm' : 'border-gray-200 hover:border-purple-300'}
                      ${isPlaceholder ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center font-bold text-white text-sm" style={{ backgroundColor: meta.color }}>
                      {meta.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 flex items-center gap-1.5">
                        {g.label}
                        {g.recommended && <span className="text-[9px] bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded font-bold">RECOMMENDED</span>}
                        {g.configured && g.name !== 'mock' && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">LIVE</span>}
                        {!g.configured && g.name !== 'mock' && <span className="text-[9px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">KEYS PENDING</span>}
                      </p>
                      <p className="text-[10px] text-gray-500 mt-0.5">{meta.tagline}</p>
                    </div>
                    {isSelected && <span className="text-purple-600 text-lg">✓</span>}
                  </button>
                );
              })}
            </div>

            {chosenAdapter && !chosenAdapter.configured && chosenAdapter.name !== 'mock' && (
              <p className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                ℹ️ Real {chosenAdapter.label} keys nahi hain — backend automatically Mock gateway pe fall back ho jayega.
              </p>
            )}

            <button
              data-testid="checkout-proceed-btn"
              onClick={startCheckout}
              disabled={busy}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 text-white px-4 py-3 rounded-xl font-bold shadow"
            >
              {busy ? 'Creating Order...' : `Proceed to Pay ${fmt(amount)}`}
            </button>
            <p className="text-[10px] text-gray-500 text-center">Secured checkout · You will be redirected to {chosenAdapter?.label || 'gateway'}</p>
          </div>
        )}

        {/* STEP: mock modal (approval-demo checkout) */}
        {step === 'mock-modal' && (
          <div className="p-4 space-y-3" data-testid="mock-checkout">
            <div className="bg-gray-100 border rounded-lg p-3 text-center">
              <p className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">🎭 Mock Checkout · Demo Mode</p>
              <p className="text-xs text-gray-600 mt-1">Real gateway keys nahi hain. Ye simulation hai — real UPI/card use nahi ho raha.</p>
            </div>

            <div className="flex gap-1 border-b">
              {['upi', 'card', 'netbanking'].map(k => (
                <button
                  key={k}
                  onClick={() => setMockChoice(k)}
                  data-testid={`mock-tab-${k}`}
                  className={`flex-1 text-xs py-2 border-b-2 font-medium ${mockChoice === k ? 'border-purple-600 text-purple-600' : 'border-transparent text-gray-500'}`}
                >
                  {k === 'upi' ? '💳 UPI' : k === 'card' ? '💳 Card' : '🏦 NetBanking'}
                </button>
              ))}
            </div>

            {mockChoice === 'upi' && (
              <div className="space-y-2">
                <label className="text-xs text-gray-600">UPI ID</label>
                <input type="text" value={mockUpi} onChange={(e) => setMockUpi(e.target.value)}
                  className="w-full border rounded px-3 py-2 font-mono text-sm" data-testid="mock-upi-input" />
                <p className="text-[10px] text-gray-500">Test UPI: demo@upi, success@razorpay (real)</p>
              </div>
            )}

            {mockChoice === 'card' && (
              <div className="space-y-2">
                <label className="text-xs text-gray-600">Card Number</label>
                <input type="text" value={mockCard} onChange={(e) => setMockCard(e.target.value)}
                  className="w-full border rounded px-3 py-2 font-mono text-sm" />
                <div className="grid grid-cols-2 gap-2">
                  <input type="text" placeholder="MM/YY" defaultValue="12/26" className="border rounded px-3 py-2 font-mono text-xs" />
                  <input type="text" placeholder="CVV" defaultValue="123" className="border rounded px-3 py-2 font-mono text-xs" />
                </div>
                <p className="text-[10px] text-gray-500">Test card: 4111 1111 1111 1111 (Razorpay test)</p>
              </div>
            )}

            {mockChoice === 'netbanking' && (
              <div className="space-y-2">
                <label className="text-xs text-gray-600">Bank</label>
                <select className="w-full border rounded px-3 py-2 text-sm">
                  <option>HDFC Bank</option>
                  <option>ICICI Bank</option>
                  <option>SBI</option>
                  <option>Axis Bank</option>
                </select>
              </div>
            )}

            <div className="bg-purple-50 border border-purple-200 rounded p-2 text-xs flex justify-between">
              <span className="text-gray-600">Amount</span>
              <span className="font-bold text-purple-900">{fmt(amount)}</span>
            </div>

            <button
              data-testid="mock-pay-btn"
              onClick={simulateMockPay}
              disabled={busy}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-4 py-3 rounded-xl font-bold"
            >
              {busy ? 'Processing...' : `✓ Simulate Successful Pay ${fmt(amount)}`}
            </button>
            <button onClick={() => setStep('choose')} className="w-full text-xs text-gray-500 hover:text-gray-800">← Back to gateway selection</button>
          </div>
        )}
      </div>
    </div>
  );
}
